import {findDOMNode} from "react-dom"
import {EditorState, Modifier} from "draft-js"
import {ENTITY_TYPE} from "../api/constants"
import {AttachmentNode} from "../../editor/plugins/attachment/node/node"

const RichEditorUtils = {

    getTypeByTrigger(trigger) {
        return trigger === '@' ? 'mention' : `${trigger}mention`;
    },

    getTypeByMention(mention) {
        let res;
        switch (mention.type) {
            case gd.const.editorLink.type.TASK: res = 'linkTask'; break;
            case gd.const.editorLink.type.NOTE: res = 'linkNote'; break;
        }
        return res;
    },

    getSearchTextAt(blockText /*string*/, position /*number*/, trigger /*string*/) {
        const str = blockText.substr(0, position);
        const begin = trigger.length === 0 ? 0 : str.lastIndexOf(trigger);
        const matchingString =
            trigger.length === 0 ? str : str.slice(begin + trigger.length);
        const end = str.length;

        return {
            begin,
            end,
            matchingString,
        };
    },

    getSearchText(editorState, selection, trigger) {
        const anchorKey = selection.getAnchorKey();
        const anchorOffset = selection.getAnchorOffset();
        const currentContent = editorState.getCurrentContent();
        const currentBlock = currentContent.getBlockForKey(anchorKey);
        const blockText = currentBlock.getText();
        return RichEditorUtils.getSearchTextAt(blockText, anchorOffset, trigger);
    },

    positionSuggestions(type, data) {
        const { decoratorRect, popover } = data;

        const getRelativeParent = (element)=>{
            if (!element)  return null;

            var position = window.getComputedStyle(element).getPropertyValue('position');
            if (position !== 'static') return element;

            return getRelativeParent(element.parentElement);
        };

        const popoverNode = findDOMNode(popover);
        const relativeParent = getRelativeParent(popoverNode.parentElement);
        const relativeRect = {};
        let left = 0, top = 0, bottom = 0;
        let res;

        switch(type) {
            case 'custom-popover-target':
                if (relativeParent) {
                    relativeRect.scrollLeft = relativeParent.scrollLeft;
                    relativeRect.scrollTop = relativeParent.scrollTop;

                    const relativeParentRect = relativeParent.getBoundingClientRect();
                    relativeRect.left = decoratorRect.left - relativeParentRect.left;
                    relativeRect.bottom = relativeParentRect.bottom - decoratorRect.bottom;
                } else {
                    relativeRect.scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                    relativeRect.scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

                    relativeRect.bottom = decoratorRect.bottom;
                    relativeRect.left = decoratorRect.left;
                }

                left = relativeRect.left + relativeRect.scrollLeft;
                bottom = relativeRect.bottom + relativeRect.scrollTop;

                res = {
                    left: left + 'px',
                    bottom: bottom + 'px',
                    display: 'block'
                };
                break;

            case 'top':
                if (relativeParent) {
                    relativeRect.scrollLeft = relativeParent.scrollLeft;
                    relativeRect.scrollTop = relativeParent.scrollTop;

                    const relativeParentRect = relativeParent.getBoundingClientRect();
                    relativeRect.left = decoratorRect.left - relativeParentRect.left;
                    relativeRect.bottom = relativeParentRect.bottom - decoratorRect.top;
                } else {
                    relativeRect.scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                    relativeRect.scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

                    relativeRect.bottom = decoratorRect.top;
                    relativeRect.left = decoratorRect.left;
                }

                left = relativeRect.left + relativeRect.scrollLeft;
                bottom = relativeRect.bottom + relativeRect.scrollTop;

                res = {
                    left: left + 'px',
                    bottom: bottom + 'px',
                    display: 'block'
                };
                break;

            case 'bottom':
                if (relativeParent) {
                    relativeRect.scrollLeft = relativeParent.scrollLeft;
                    relativeRect.scrollTop = relativeParent.scrollTop;

                    const relativeParentRect = relativeParent.getBoundingClientRect();
                    relativeRect.left = decoratorRect.left - relativeParentRect.left;
                    relativeRect.top = decoratorRect.bottom - relativeParentRect.top;
                } else {
                    relativeRect.scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                    relativeRect.scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

                    relativeRect.top = decoratorRect.bottom;
                    relativeRect.left = decoratorRect.left;
                }

                left = relativeRect.left + relativeRect.scrollLeft;
                top = relativeRect.top + relativeRect.scrollTop;

                res = {
                    left: left + 'px',
                    top: top + 'px',
                    display: 'block'
                };
                break;
        }

        return res;
    },

    addMention(editorState, mention, mentionPrefix, mentionTrigger) {
        const contentStateWithEntity = editorState
            .getCurrentContent()
            .createEntity(RichEditorUtils.getTypeByMention(mention), "IMMUTABLE", {
                mention,
            });
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

        const currentSelectionState = editorState.getSelection();
        const { begin, end } = RichEditorUtils.getSearchText(
            editorState,
            currentSelectionState,
            mentionTrigger
        );

        // get selection of the @mention search text
        const mentionTextSelection = currentSelectionState.merge({
            anchorOffset: begin,
            focusOffset: end,
        });

        let mentionReplacedContent = Modifier.replaceText(
            editorState.getCurrentContent(),
            mentionTextSelection,
            `${mentionPrefix}${mention.name}`,
            null, // no inline style needed
            entityKey
        );

        // If the mention is inserted at the end, a space is appended right after for
        // a smooth writing experience.
        const blockKey = mentionTextSelection.getAnchorKey();
        const blockSize = editorState
            .getCurrentContent()
            .getBlockForKey(blockKey)
            .getLength();
        if (blockSize === end) {
            mentionReplacedContent = Modifier.insertText(
                mentionReplacedContent,
                mentionReplacedContent.getSelectionAfter(),
                ' '
            );
        }

        const newEditorState = EditorState.push(
            editorState,
            mentionReplacedContent,
            'insert-mention'
        );
        return EditorState.forceSelection(
            newEditorState,
            mentionReplacedContent.getSelectionAfter()
        );
    },

    replaceText(editorState, textToReplace = '/', newText = '') {
        const currentSelectionState = editorState.getSelection();
        const { begin, end } = RichEditorUtils.getSearchText(
            editorState,
            currentSelectionState,
            textToReplace
        );

        // get selection of the @mention search text
        const mentionTextSelection = currentSelectionState.merge({
            anchorOffset: begin,
            focusOffset: end,
        });

        let contentState = editorState.getCurrentContent();

        let mentionReplacedContent = Modifier.replaceText(
            contentState,
            mentionTextSelection,
            newText,
            null, // no inline style needed
        );

        return  EditorState.push(
            editorState,
            mentionReplacedContent
        );
    },

    attachmentsInMessage(messageRTF) {
        let res = [];
        if (messageRTF) {
            _.map(messageRTF.entityMap, e=>{
                if (e.type === ENTITY_TYPE.ATTACHMENT) {
                    res.push(e.data.fileId);
                }
            })
        }
        return res;
    },

    // returns the raw attachment data (fileId, name, mime, ...) of every attachment node in the
    // content, so a caller can rebuild an Attachment for one the collection lost track of
    attachmentsInLexicalMessage(messageRTF) {
        let res = []
        const findAttachments = (node, attachments = []) => {
            if (node?.type === AttachmentNode.getType()) attachments.push(node.attachment)

            _.map(node?.children, childNode => {
                attachments = findAttachments(childNode, attachments)
            })

            return attachments
        }

        res = findAttachments(messageRTF.root)
        return res;
    },

    contentToTextWithoutNewLine(content) {

        if (!content || !content.blocks) return ''

        const contentText = content.blocks.reduce(
            (text, block)=>{
                // text eto chto uze est' (previous iteration
                // block new block

                // here each new block = new line

                return (text ? text + ' ' : '') + block.text
            }
            , ''
        )

        return contentText
    },

    contentToTextWithNewLine(content) {

        if (!content || !content.blocks) return ''


        const contentText = content.blocks.reduce(
            (text, block)=>{
                // text eto chto uze est' (previous iteration
                // block new block

                // here each new block = new line

                return (text ? text + '' : '') + block.text + '\n'
            }
            , ''
        )

        return contentText
    },

    addText(editorState, newText = '') {
        // get current editor state
        const currentContent = editorState.getCurrentContent()

        // create new selection state where focus is at the end
        const currentSelectionState = editorState.getSelection()

        //insert text at the selection created above
        const textWithInsert = Modifier.insertText(currentContent, currentSelectionState, newText, null)
        const editorWithInsert = EditorState.push(editorState, textWithInsert, 'insert-characters')

        //also focuses cursor at the end of the editor
        const newEditorState = EditorState.moveSelectionToEnd(editorWithInsert)

        return newEditorState
    },
};

module.exports = RichEditorUtils;